import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { setVizLoading } from '../../../../store/visualizerSlice';
import ProductGroupStep from '../ProductGroupStep/ProductGroupStep';
import StepNavigation from '../../StepNavigation/StepNavigation';
import PatternTab from '../BathFloor/PatternTab';
import styles from './ShowerWall.module.css';

const TABS = ['Tile', 'Pattern', 'Grout'];
const DEFAULT_PATTERN_ID = 201858;
const DEFAULT_GROUT_ID = -1;
const FIXED_ARG3 = 147607;
// Rotation code — 254252 for horizontal lays, 254254 for vertical lays.
const ARG4_HORIZONTAL = 254252;
const ARG4_VERTICAL = 254254;
function isVerticalPattern(pattern) {
    const type = String(pattern?.type || '').toLowerCase();
    const name = String(pattern?.name || '').toLowerCase();
    return type.includes('vertical') || name.includes('vertical');
}

function applyPatterned(patternId, tileId, groutId, surfaceName, dispatch, vertical = false) {
    const chamViz = document.getElementById('chamViz');
    if (!chamViz || !patternId) return;

    const arg4 = vertical ? ARG4_VERTICAL : ARG4_HORIZONTAL;
    const sel = `${patternId}(${tileId},${groutId},${FIXED_ARG3},${arg4})`;
    dispatch(setVizLoading(true));

    chamViz.applyByFilter(
        patternId,
        '',
        function() {
            if (this.name === surfaceName) return sel;
            return this.sel;
        },
        true
    );
}

export default function ShowerWall({
    surfaceName = 'Shower Wall',
    stepIndex,
    room = '',
    onViewAllApplied,
    onNext,
    onPrevious
}) {
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState('Tile');
    const [patternId, setPatternId] = useState(DEFAULT_PATTERN_ID);
    const [isVertical, setIsVertical] = useState(false);
    const [tileId, setTileId] = useState(null);
    const [groutId, setGroutId] = useState(DEFAULT_GROUT_ID);

    const handleTileSelect = useCallback((product) => {
        setTileId(product.id);
        applyPatterned(patternId, product.id, groutId, surfaceName, dispatch, isVertical);
    }, [patternId, groutId, surfaceName, dispatch, isVertical]);

    const handlePatternSelect = useCallback((pattern) => {
        setPatternId(pattern.id);
        const vertical = isVerticalPattern(pattern);
        setIsVertical(vertical);
        if (!tileId) return;
        applyPatterned(pattern.id, tileId, groutId, surfaceName, dispatch, vertical);
    }, [tileId, groutId, surfaceName, dispatch]);

    const handleGroutSelect = useCallback((product) => {
        setGroutId(product.id);
        if (!tileId) return;
        applyPatterned(patternId, tileId, product.id, surfaceName, dispatch, isVertical);
    }, [patternId, tileId, surfaceName, dispatch, isVertical]);

    return (
        <div className={styles.wrapper}>
            <StepNavigation
                title="Shower Wall"
                onNext={onNext}
                onPrevious={onPrevious}
                onViewAllApplied={onViewAllApplied}
            />

            <div className={styles.tabs}>
                {TABS.map(tab => (
                    <button
                        key={tab}
                        className={`${styles.tab} ${activeTab === tab ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab(tab)}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            <div className={styles.tabContent}>
                {activeTab === 'Tile' && (
                    <div className={styles.productArea}>
                        <ProductGroupStep
                            title=""
                            stepName="ShowerWall"
                            seriesId="9784"
                            surfaceName={surfaceName}
                            stepIndex={stepIndex}
                            room={room}
                            onViewAllApplied={onViewAllApplied}
                            onNext={onNext}
                            onPrevious={onPrevious}
                            hideNav
                            onProductSelect={handleTileSelect}
                        />
                    </div>
                )}

                {activeTab === 'Pattern' && (
                    <PatternTab
                        selectedPatternId={patternId}
                        onPatternSelect={handlePatternSelect}
                    />
                )}

                {activeTab === 'Grout' && (
                    <div className={styles.productArea}>
                        <ProductGroupStep
                            title=""
                            stepName="ShowerWallGrout"
                            seriesId="8311"
                            surfaceName={surfaceName}
                            stepIndex={stepIndex}
                            room={room}
                            onViewAllApplied={onViewAllApplied}
                            onNext={onNext}
                            onPrevious={onPrevious}
                            hideNav
                            noFilter
                            onProductSelect={handleGroutSelect}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

ShowerWall.propTypes = {
    surfaceName: PropTypes.string,
    stepIndex: PropTypes.number.isRequired,
    room: PropTypes.string,
    onViewAllApplied: PropTypes.func,
    onNext: PropTypes.func,
    onPrevious: PropTypes.func
};
